// House promo — the ad slot, v1 (spec §1). A FIRST-PARTY creative for our own
// game site, drawn inline from this origin: no ad network, no tracker, no
// network request, CSP untouched. (The sandboxed-iframe isolation is for
// third-party ad tags only — if one ever ships, it goes back into an iframe.)
//
// One of four creatives is picked uniformly at random per page load; the
// creative id travels as `kind` on house_ad_view / house_ad_click so CTR per
// design is queryable in WAE. The outbound link carries ONLY ?ref=cruxdiff for
// landing attribution — deliberately no creative param (cleaner URL; the
// per-creative split lives in our own analytics, not the destination's).
import { track } from '../lib/analytics.js';

export const PROMO_URL = 'https://timewelllost.com/?ref=cruxdiff';

// "Deal yourself in" — playing cards fanned at the cruxdiff mark's tilt angles.
const artCards = `<svg width="112" height="84" viewBox="0 0 120 90" aria-hidden="true" focusable="false">
  <rect x="16" y="18" width="34" height="50" rx="5" fill="var(--panel-2)" stroke="var(--faint)" stroke-width="1.6" transform="rotate(-14 33 43)"/>
  <g transform="rotate(-2 57 41)">
    <rect x="40" y="16" width="34" height="50" rx="5" fill="var(--panel)" stroke="var(--line)" stroke-width="1.6"/>
    <text x="57" y="48" text-anchor="middle" font-size="17" fill="var(--del)">♥</text>
  </g>
  <g transform="rotate(12 79 43)">
    <rect x="62" y="18" width="34" height="50" rx="5" fill="var(--amber)"/>
    <text x="79" y="49" text-anchor="middle" font-size="18" fill="var(--panel)">♠</text>
    <text x="69" y="31" font-size="8" font-weight="700" fill="var(--panel)">A</text>
  </g>
</svg>`;

// "A diff you'll enjoy" — the pitch written in cruxdiff's own visual grammar.
const artDiff = `<span class="promo-diff" aria-hidden="true">
  <span class="row del"><span class="sg">−</span>20 spare minutes</span>
  <span class="row add"><span class="sg">+</span>time well lost</span>
</span>`;

// "Your move" — a board where the target cell glows like a changed cell.
const artBoard = `<svg width="112" height="76" viewBox="0 0 130 88" aria-hidden="true" focusable="false">
  <rect x="13" y="8" width="104" height="72" rx="6" fill="var(--panel)" stroke="var(--line)" stroke-width="1.5"/>
  <rect x="39" y="8" width="26" height="24" fill="var(--panel-2)"/>
  <rect x="13" y="32" width="26" height="24" fill="var(--panel-2)"/>
  <rect x="65" y="32" width="26" height="24" fill="var(--panel-2)"/>
  <rect x="39" y="56" width="26" height="24" fill="var(--panel-2)"/>
  <rect x="91" y="56" width="26" height="24" fill="var(--panel-2)"/>
  <rect x="91" y="8" width="26" height="24" fill="var(--amber-soft)" stroke="var(--amber-line)" stroke-width="1.5" stroke-dasharray="4 3"/>
  <circle cx="26" cy="68" r="8.5" fill="var(--panel)" stroke="var(--text)" stroke-width="2"/>
  <circle cx="52" cy="44" r="8.5" fill="var(--amber)"/>
  <path d="M62 38 Q80 16 98 21" fill="none" stroke="var(--amber)" stroke-width="1.8" stroke-dasharray="4 3.5"/>
  <path d="M99.5 15.5 L104 21.5 L96.5 22.5 Z" fill="var(--amber)"/>
  <text x="78" y="73" text-anchor="middle" font-size="12" fill="var(--faint)">5</text>
</svg>`;

// "Time well lost" — TWL's verbatim tagline + a tilted hourglass, amber sand.
const artHourglass = `<svg width="72" height="76" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
  <g transform="rotate(-8 50 50)">
    <rect x="27" y="12" width="46" height="6" rx="2.5" fill="var(--text)"/>
    <rect x="27" y="82" width="46" height="6" rx="2.5" fill="var(--text)"/>
    <path d="M33 20 L67 20 L67 28 C67 38 57 43 52.5 48 L50.5 50 L52.5 52 C57 57 67 62 67 72 L67 80 L33 80 L33 72 C33 62 43 57 47.5 52 L49.5 50 L47.5 48 C43 43 33 38 33 28 Z" fill="none" stroke="var(--muted)" stroke-width="2" stroke-linejoin="round"/>
    <path d="M42 36 L58 36 L50.6 44.5 Z" fill="var(--amber)" opacity=".55"/>
    <path d="M50 63 L61.5 77 L38.5 77 Z" fill="var(--amber)"/>
    <rect x="49.3" y="52" width="1.9" height="1.9" fill="var(--amber)"/>
    <rect x="49.3" y="57" width="1.9" height="1.9" fill="var(--amber)"/>
  </g>
</svg>`;

export const CREATIVES = [
  { id: 'cards', art: artCards, head: 'Done comparing? Deal yourself in.', sub: 'Callbreak, Spades, Backgammon &amp; more — smart AI, zero friction.', cta: 'Play free →' },
  { id: 'diffpun', art: artDiff, head: 'A diff you&#8217;ll enjoy.', sub: 'Seven classic games in your browser. No accounts, no stakes.', cta: 'Apply patch →' },
  { id: 'move', art: artBoard, head: 'Your move.', sub: 'Checkers, Backgammon, Sudoku &amp; more — against AI that plays fair.', cta: 'Play free →' },
  { id: 'hourglass', art: artHourglass, head: 'Time you enjoy losing is time well lost.', sub: 'Seven classic games, free in your browser.', cta: 'Lose some →' },
];

/** Uniform random creative; injectable rand for tests. */
export function pickCreative(rand = Math.random) {
  return CREATIVES[Math.floor(rand() * CREATIVES.length) % CREATIVES.length];
}

/** The promo card — whole card is the link; honestly labeled with a PROMO chip. */
export function promo(c = pickCreative()) {
  return `
  <section class="promo-slot wrap">
    <a class="promo" data-creative="${c.id}" href="${PROMO_URL}" target="_blank" rel="noopener"
       aria-label="Promo: TimeWellLost — seven classic games, free in your browser">
      <span class="promo-chip" aria-hidden="true">PROMO</span>
      <span class="promo-art">${c.art}</span>
      <span class="promo-copy">
        <span class="promo-dom">timewelllost.com</span>
        <span class="promo-head">${c.head}</span>
        <span class="promo-sub">${c.sub}</span>
      </span>
      <span class="promo-cta">${c.cta}</span>
    </a>
  </section>`;
}

/** Beacon the impression once and the click when it happens (page events only). */
export function initPromo(report = track) {
  const el = document.querySelector('.promo');
  if (!el) return;
  const kind = el.dataset.creative;
  report('house_ad_view', { kind, extra: 'timewelllost' });
  el.addEventListener('click', () => report('house_ad_click', { kind, extra: 'timewelllost' }));
}
